import React, { useState, useEffect, useRef } from "react";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";

interface GrandChild {
  id: string;
  name: string;
  href: string;
}

interface Child {
  id: string;
  name: string;
  href: string;
  grand_childs?: GrandChild[];
}

interface Category {
  id: string;
  name: string;
  href: string;
  children?: Child[];
}

interface TreeSelectProps {
  data: Category[];
  onCategoryChange: (category: string) => void;
}

const TreeSelect: React.FC<TreeSelectProps> = ({ data, onCategoryChange }) => {
  const { i18n } = useLingui();
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleSelect = (value: string) => {
    setSelectedValue(value);
    onCategoryChange(value);
    setIsOpen(false);
  };

  const getSelectedName = (id: string): string => {
    if (!id) return t(i18n)`All Categories`;

    for (const category of data) {
      if (category.id === id) return category.name;
      if (category.children) {
        for (const child of category.children) {
          if (child.id === id) return child.name;
          if (child.grand_childs) {
            for (const grandChild of child.grand_childs) {
              if (grandChild.id === id) return grandChild.name;
            }
          }
        }
      }
    }
    return id;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const generateListItems = (data: Category[]) => {
    const items: JSX.Element[] = [];

    items.push(
      <li
        key="none"
        onClick={() => handleSelect("")}
        className="cursor-pointer px-4 py-2 hover:bg-gray-100"
      >
        {t(i18n)`All Categories`}
      </li>,
    );

    data.forEach((category) => {
      items.push(
        <li
          key={category.id}
          onClick={() => handleSelect(category.id)}
          className="cursor-pointer px-4 py-2 font-bold hover:bg-gray-100"
        >
          {category.name}
        </li>,
      );

      if (category.children) {
        category.children.forEach((child) => {
          items.push(
            <li
              key={child.id}
              onClick={() => handleSelect(child.id)}
              className="cursor-pointer p-2 pl-[40px] hover:bg-gray-100"
            >
              {child.name}
            </li>,
          );

          if (child.grand_childs) {
            child.grand_childs.forEach((grandChild) => {
              items.push(
                <li
                  key={grandChild.id}
                  onClick={() => handleSelect(grandChild.id)}
                  className="cursor-pointer p-2 pl-[60px] hover:bg-gray-100"
                >
                  {grandChild.name}
                </li>,
              );
            });
          }
        });
      }
    });

    return items;
  };

  return (
    <div ref={containerRef} className="relative inline-block w-full md:w-auto">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-[50px] w-full items-center justify-between rounded-lg border border-[rgba(0,0,0,0.23)] px-4 md:w-auto"
      >
        <span>{getSelectedName(selectedValue)}</span>
        <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <ul className="absolute left-0 right-0 z-10 mt-1 max-h-[50vh] w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow md:w-[350px]">
          {generateListItems(data)}
        </ul>
      )}
    </div>
  );
};

export default TreeSelect;
